import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { createNoticeStep, updateNoticeStep } from "@/features/notices/api";
import type { NoticeStep } from "@/features/notices/types";
import { applyApiErrors } from "@/lib/form-errors";

interface StepFormValues {
  description: string;
  dueDate: string;
}

const validationSchema = Yup.object({
  description: Yup.string().trim().required("A step description is required"),
});

interface NoticeStepDialogProps {
  noticeId: string;
  /** `null` = create a new step; otherwise edit this one. */
  step: NoticeStep | null;
  nextPosition: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Create/edit a single notice action step: description + due date. */
export function NoticeStepDialog({
  noticeId,
  step,
  nextPosition,
  open,
  onOpenChange,
  onSaved,
}: NoticeStepDialogProps) {
  const editing = step !== null;

  async function handleSubmit(values: StepFormValues, bag: FormikHelpers<StepFormValues>) {
    const payload = {
      description: values.description.trim(),
      due_date: values.dueDate || null,
    };
    try {
      if (editing) {
        await updateNoticeStep(noticeId, step.id, payload);
      } else {
        await createNoticeStep(noticeId, { ...payload, position: nextPosition });
      }
      toast.success(editing ? "Step updated" : "Step added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save step." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Step" : "Add Step"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={{ description: step?.description ?? "", dueDate: step?.dueDate ?? "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField
                name="description"
                label="Step"
                labelClassName={FIELD_LABEL}
                placeholder="e.g. File Form 12153 (CDP) within 30 days"
              />
              <FormField name="dueDate" label="Due Date" labelClassName={FIELD_LABEL} type="date" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Add Step"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
